import { createSelector } from 'reselect';

import {
  PLAN_INTERVAL,
  PRODUCT_LIVE,
  PRODUCT_API_TESTING,
  PRODUCT_RDC,
  PRODUCT_VDC,
  PRODUCT_MOBILE_QUALITY,
  PRODUCT_USAGE,
  PLAN_CODE_INTERNVAL,
  UNIFIED_BILLING_TYPE,
  USAGE_TYPE,
} from '../../constants';
import { Plan } from '../../records/Plan';
import { State } from './reducers';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const getStore = (store) => store.apps.billing.plans as State;
const getPlans = (store) => getStore(store).elements;

const getIsLoadingSelector = createSelector(getStore, (store: State) => store.isLoading);

const filterUnifiedPlans = (plan: Plan) => plan.plan_type === UNIFIED_BILLING_TYPE;
const filterUsagePlans = (plan: Plan) => plan.plan_type === USAGE_TYPE;
/* eslint-disable camelcase */
const filterLiveMonthly = ({ plan_code }: Plan) => (
  plan_code.includes(PRODUCT_LIVE) && plan_code.endsWith(PLAN_CODE_INTERNVAL.MONTHLY)
);
const filterLiveAnnual = ({ plan_code }: Plan) => (
  plan_code.includes(PRODUCT_LIVE) && plan_code.endsWith(PLAN_CODE_INTERNVAL.ANNUAL)
);

const filterRdcMonthly = ({ plan_code }: Plan) => (
  plan_code.includes(PRODUCT_RDC) && plan_code.endsWith(PLAN_CODE_INTERNVAL.MONTHLY)
);
const filterRdcAnnual = ({ plan_code }: Plan) => (
  plan_code.includes(PRODUCT_RDC) && plan_code.endsWith(PLAN_CODE_INTERNVAL.ANNUAL)
);
const filterVdcMonthly = ({ plan_code }: Plan) => (
  plan_code.includes(PRODUCT_VDC) && plan_code.endsWith(PLAN_CODE_INTERNVAL.MONTHLY)
);
const filterVdcAnnual = ({ plan_code }: Plan) => (
  plan_code.includes(PRODUCT_VDC) && plan_code.endsWith(PLAN_CODE_INTERNVAL.ANNUAL)
);
const filterMobileQualityMonthly = ({ plan_code }: Plan) => (
  plan_code.includes(PRODUCT_MOBILE_QUALITY) && plan_code.endsWith(PLAN_CODE_INTERNVAL.MONTHLY)
);
const filterMobileQualityAnnual = ({ plan_code }: Plan) => (
  plan_code.includes(PRODUCT_MOBILE_QUALITY) && plan_code.endsWith(PLAN_CODE_INTERNVAL.ANNUAL)
);
const filterApiTestingMonthly = ({ plan_code }: Plan) => (
  plan_code.includes(PRODUCT_API_TESTING) && plan_code.endsWith(PLAN_CODE_INTERNVAL.MONTHLY)
);
const filterApiTestingAnnual = ({ plan_code }: Plan) => (
  plan_code.includes(PRODUCT_API_TESTING) && plan_code.endsWith(PLAN_CODE_INTERNVAL.ANNUAL)
);
const filterUsageMonthly = ({ plan_code }: Plan) => (
  plan_code.includes(PRODUCT_USAGE) && plan_code.endsWith(PLAN_CODE_INTERNVAL.MONTHLY)
);

const getStartingPrice = (plan: Plan) => plan.calculateMonthlyRate(1);

export const getUnifiedPlansSelector = createSelector(
  getPlans,
  (plans) => plans.filter(filterUnifiedPlans),
);

export const getUsagePlans = createSelector(
  getPlans,
  (plans) => plans.filter(filterUsagePlans),
);

const getUsageCreditsPlansSelector = createSelector(
  getUsagePlans,
  (plans) => ({
    [PLAN_INTERVAL.MONTH]: plans.find(filterUsageMonthly),
    [PLAN_INTERVAL.YEAR]: plans.find(filterUsageMonthly),
  }),
);

const getUnifiedLivePlansSelector = createSelector(
  getUnifiedPlansSelector,
  (unifiedPlans) => ({
    [PLAN_INTERVAL.MONTH]: unifiedPlans.find(filterLiveMonthly),
    [PLAN_INTERVAL.YEAR]: unifiedPlans.find(filterLiveAnnual),
  }),
);

const getUnifiedRdcPlansSelector = createSelector(
  getUnifiedPlansSelector,
  (unifiedPlans) => ({
    [PLAN_INTERVAL.MONTH]: unifiedPlans.find(filterRdcMonthly),
    [PLAN_INTERVAL.YEAR]: unifiedPlans.find(filterRdcAnnual),
  }),
);

const getUnifiedVdcPlansSelector = createSelector(
  getUnifiedPlansSelector,
  (unifiedPlans) => ({
    [PLAN_INTERVAL.MONTH]: unifiedPlans.find(filterVdcMonthly),
    [PLAN_INTERVAL.YEAR]: unifiedPlans.find(filterVdcAnnual),
  }),
);

const getUnifiedMobileQualityPlansSelector = createSelector(
  getUnifiedPlansSelector,
  (unifiedPlans) => ({
    [PLAN_INTERVAL.MONTH]: unifiedPlans.find(filterMobileQualityMonthly),
    [PLAN_INTERVAL.YEAR]: unifiedPlans.find(filterMobileQualityAnnual),
  }),
);

const getUnifiedApiTestingPlansSelector = createSelector(
  getUnifiedPlansSelector,
  (unifiedPlans) => ({
    [PLAN_INTERVAL.MONTH]: unifiedPlans.find(filterApiTestingMonthly),
    [PLAN_INTERVAL.YEAR]: unifiedPlans.find(filterApiTestingAnnual),
  }),
);

const getStartingPlansSelector = createSelector(
  getIsLoadingSelector, getUnifiedPlansSelector, getUsagePlans,
  (isLoading, unifiedPlans, usagePlans) => {
    if (isLoading) {
      return { live: null, automated: null, mobileQuality: null, apiTesting: null };
    }
    const live = unifiedPlans.find(filterLiveAnnual) as Plan;
    const automated = unifiedPlans.find(filterVdcAnnual) as Plan;
    const mobileQuality = unifiedPlans.find(filterMobileQualityAnnual) as Plan;
    const apiTesting = unifiedPlans.find(filterApiTestingAnnual) as Plan;
    const usage = usagePlans.find(filterUsageMonthly) as Plan;

    return { live, automated, mobileQuality, apiTesting, usage };
  },
);

const getStartingPricesSelector = createSelector(
  getIsLoadingSelector, getStartingPlansSelector,
  (isLoading, plans) => {
    if (isLoading) {
      return { live: null, automated: null, mobileQuality: null, apiTesting: null, usage: null };
    }
    return {
      live: plans.live && getStartingPrice(plans.live),
      automated: plans.automated && getStartingPrice(plans.automated),
      mobileQuality: plans.mobileQuality && getStartingPrice(plans.mobileQuality),
      apiTesting: plans.apiTesting && getStartingPrice(plans.apiTesting),
      usage: plans.usage && getStartingPrice(plans.usage),
    };
  },
);

const getNonLegacyPlans = createSelector(
  getPlans,
  (plans) => [...plans.filter(filterUnifiedPlans), ...plans.filter(filterUsagePlans)],
);

export {
  getStartingPlansSelector,
  getStartingPricesSelector,
  getUnifiedLivePlansSelector,
  getUnifiedRdcPlansSelector,
  getUnifiedVdcPlansSelector,
  getUnifiedMobileQualityPlansSelector,
  getUnifiedApiTestingPlansSelector,
  getUsageCreditsPlansSelector,
  getNonLegacyPlans,
};
